import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CandlestickChart, Download, Radio } from "lucide-react";
import { getBars, getLiveTail, type BarOut } from "../api/bars";
import {
  ALL_TIMEFRAMES,
  backfillInstrument,
  createGapFill,
  getBackfill,
  type BackfillJob,
  type SkippedTimeframe,
} from "../api/data";
import {
  getInstrumentCoverage,
  resolveInstrument,
  searchInstruments,
  type MasterInstrumentOut,
  type StoredInstrumentOut,
} from "../api/instruments";
import { getStrategy, listStrategies } from "../api/strategies";
import { apiErrorMessage } from "../lib/api";
import Button from "../components/Button";
import EmptyState from "../components/EmptyState";
import IndicatorsPanel from "../components/IndicatorsPanel";
import PriceChart from "../components/PriceChart";
import Skeleton from "../components/Skeleton";
import SymbolSearch, { type PickedInstrument } from "../components/SymbolSearch";
import { sessionDensityFactor, tfMillis } from "../lib/chartInstruments";
import {
  buildInstanceFromSpec,
  type IndicatorInstance,
} from "../lib/indicators";
import { isGoldenFamily } from "../lib/goldenFamily";

// How many bars the initial window targets. Below /bars' 100K downsampling
// cap by a wide margin, and plenty for on-screen scrollback.
const INITIAL_BARS = 1_000;
const LIVE_POLL_MS = 5_000;
const LAST_VIEWED_KEY = "trade1.chart.lastViewed";

/** What the download dialog is staged to fetch. `timeframes: null` means all
 *  eight; a list means "just these", which is how "Get all timeframes" asks
 *  only for the ones a symbol is missing. */
interface DownloadTarget {
  id: string;
  symbol: string;
  exchange: string;
  instrument_type: string;
  expiry: string | null;
  strike: string | null;
  lot_size?: number;
  tick_size?: string;
  timeframes: string[] | null;
  label: string;
}

interface BootstrapNote {
  detail: string | null;
  skipped: SkippedTimeframe[];
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

// Stable empty reference for PriceChart's `trades` prop. Inline `[]` literals
// get a new identity every render — and the live poll re-renders this page
// every 5s. `trades` feeds PriceChart's marker effect, so a fresh `[]` each
// render made it re-run needlessly. A single shared reference keeps that
// effect stable across the live poll.
const EMPTY: never[] = [];

export default function ChartPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // The picker is server-backed now, so the chart no longer downloads the
  // whole coverage table (4.28 MB / 17,566 rows) just to populate a dropdown.
  // It resolves the ONE instrument it is showing, plus that instrument's
  // timeframes.
  const urlSymbol = searchParams.get("symbol");
  const urlTf = searchParams.get("tf");

  // Fallback chain: URL -> localStorage -> the server's featured default.
  const remembered = useMemo(() => {
    try {
      const raw = localStorage.getItem(LAST_VIEWED_KEY);
      return raw ? (JSON.parse(raw) as { symbol?: string }).symbol ?? null : null;
    } catch {
      return null;
    }
  }, []);
  const wantedId = urlSymbol ?? remembered;

  const resolved = useQuery({
    queryKey: ["instrument-resolve", wantedId],
    queryFn: () => resolveInstrument(wantedId!),
    enabled: !!wantedId,
    staleTime: 300_000,
    retry: false, // a stale bookmark shouldn't retry; fall through to the default
  });

  // Only when nothing is remembered or the remembered id no longer exists.
  // The old code fell back to instruments[0] after an id sort — an arbitrary
  // NFO option strike.
  const fallback = useQuery({
    queryKey: ["instrument-featured"],
    queryFn: () => searchInstruments("", 1),
    enabled: !wantedId || resolved.isError,
    staleTime: 300_000,
  });

  const selected: StoredInstrumentOut | null =
    resolved.data ?? fallback.data?.stored[0] ?? null;

  const coverageQuery = useQuery({
    queryKey: ["instrument-coverage", selected?.id],
    queryFn: () => getInstrumentCoverage(selected!.id),
    enabled: !!selected,
    staleTime: 30_000,
  });
  const tfRows = coverageQuery.data ?? [];

  const availableTfs = selected?.timeframes ?? [];
  const timeframe =
    urlTf && availableTfs.includes(urlTf)
      ? urlTf
      : (availableTfs[availableTfs.length - 1] ?? "1h"); // default: coarsest

  // Persist + normalize URL whenever the effective selection changes.
  useEffect(() => {
    if (!selected) return;
    if (urlSymbol !== selected.id || urlTf !== timeframe) {
      setSearchParams(
        { symbol: selected.id, tf: timeframe },
        { replace: true },
      );
    }
    localStorage.setItem(
      LAST_VIEWED_KEY,
      JSON.stringify({ symbol: selected.id, tf: timeframe }),
    );
  }, [selected, timeframe, urlSymbol, urlTf, setSearchParams]);

  function selectInstrument(inst: PickedInstrument) {
    const tfs = inst.timeframes;
    const nextTf = tfs.includes(timeframe)
      ? timeframe
      : (tfs[tfs.length - 1] ?? "1h");
    setSearchParams({ symbol: inst.id, tf: nextTf });
  }

  // --- Download / fill missing timeframes ----------------------------------
  // A brand-new instrument needs a FULL backfill, not a gap-fill: gap-fill only
  // tops up timeframes that already have bars, so on a symbol with none it
  // returns zero jobs — which is exactly why this button used to do nothing.
  // How much history to pull is a real cost decision, so it is asked for
  // rather than guessed.
  const [downloadTarget, setDownloadTarget] = useState<DownloadTarget | null>(null);
  const [bootstrapJobIds, setBootstrapJobIds] = useState<string[]>([]);
  const [bootstrapNote, setBootstrapNote] = useState<BootstrapNote | null>(null);

  function downloadInstrument(inst: MasterInstrumentOut) {
    setDownloadTarget({
      id: inst.id,
      symbol: inst.symbol,
      exchange: inst.exchange,
      instrument_type: inst.instrument_type,
      expiry: inst.expiry,
      strike: inst.strike,
      lot_size: inst.lot_size,
      tick_size: inst.tick_size,
      timeframes: null, // all eight
      label: inst.display,
    });
  }

  // Timeframes this symbol is missing — drives the "Get all timeframes" action.
  const missingTfs = useMemo(
    () => ALL_TIMEFRAMES.filter((tf) => !availableTfs.includes(tf)),
    [availableTfs],
  );

  function getMissingTimeframes() {
    if (!selected || missingTfs.length === 0) return;
    setDownloadTarget({
      id: selected.id,
      symbol: selected.symbol,
      exchange: selected.exchange,
      instrument_type: selected.instrument_type,
      expiry: selected.expiry,
      strike: selected.strike,
      lot_size: selected.lot_size,
      tick_size: selected.tick_size,
      timeframes: [...missingTfs],
      label: selected.display,
    });
  }

  const bootstrap = useMutation({
    mutationFn: backfillInstrument,
    onSuccess: (res, vars) => {
      setBootstrapJobIds(res.jobs.map((j) => j.id));
      setBootstrapNote({ detail: res.detail, skipped: res.skipped });
      // Switch to the symbol straight away rather than waiting for data — the
      // old flow left you on the previous chart with no sign anything happened.
      if (res.jobs.length > 0 && vars.symbol !== selected?.symbol) {
        setSearchParams({ symbol: downloadTarget?.id ?? vars.symbol });
      }
      setDownloadTarget(null);
    },
    onError: (e) => {
      setBootstrapNote({ detail: apiErrorMessage(e), skipped: [] });
      setDownloadTarget(null);
    },
  });

  // Poll every scheduled job. They run strictly one at a time (the runner is
  // serialised) in the coarsest-first order the backend queued them, so `1d`
  // lands first — which is what lets the chart paint while the rest continue.
  const bootstrapJobs = useQuery({
    queryKey: ["bootstrap-jobs", bootstrapJobIds.join(",")],
    queryFn: () => Promise.all(bootstrapJobIds.map((id) => getBackfill(id))),
    enabled: bootstrapJobIds.length > 0,
    refetchInterval: (q) => {
      const jobs = (q.state.data ?? []) as BackfillJob[];
      const active = jobs.some(
        (j) => j.status === "PENDING" || j.status === "RUNNING",
      );
      return active ? 2_000 : false;
    },
  });

  // As each timeframe lands, make it visible. Keyed on the set of finished
  // timeframes so it re-runs per completion, not just once at the end.
  const doneTfs = (bootstrapJobs.data ?? [])
    .filter((j) => j.status === "SUCCESS")
    .map((j) => j.timeframe)
    .join(",");
  useEffect(() => {
    if (!doneTfs) return;
    qc.invalidateQueries({ queryKey: ["instrument-coverage", selected?.id] });
    qc.invalidateQueries({ queryKey: ["instrument-search"] });
    qc.invalidateQueries({ queryKey: ["chart-bars"] });
    // Daily finishes first and is the cheapest thing to draw — show it as soon
    // as it exists rather than leaving the user on an empty chart.
    if (doneTfs.split(",").includes("1d") && selected && !availableTfs.includes(timeframe)) {
      setSearchParams({ symbol: selected.id, tf: "1d" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneTfs]);

  function selectTimeframe(tf: string) {
    if (!selected) return;
    setSearchParams({ symbol: selected.id, tf });
  }

  // --- Live toggle ---------------------------------------------------------
  const [live, setLive] = useState(true);
  const qc = useQueryClient();

  // --- Initial bars from the store ----------------------------------------
  const tfRow = tfRows.find((t) => t.timeframe === timeframe);
  const barsQuery = useQuery({
    queryKey: ["chart-bars", selected?.id, timeframe],
    queryFn: () => {
      const lastTs = tfRow?.last_ts ? new Date(tfRow.last_ts + "Z") : new Date();
      // "Last N bars" in wall-clock time, scaled by session density —
      // NSE trades ~6.25h/day, so N bars span ~4x more wall-clock time
      // than on a 24/7 market. Older history lazy-loads on scroll.
      const windowMs =
        INITIAL_BARS * tfMillis(timeframe) * sessionDensityFactor(selected!.exchange);
      const start = new Date(lastTs.getTime() - windowMs);
      // End slightly in the future so live-appended bars stay in range.
      const end = new Date(Date.now() + 60 * 60 * 1000);
      return getBars({
        symbol: selected!.symbol,
        exchange: selected!.exchange,
        timeframe,
        instrument_type: selected!.instrument_type,
        expiry: selected!.expiry,
        strike: selected!.strike,
        start: start.toISOString(),
        end: end.toISOString(),
      });
    },
    enabled: !!selected && !!tfRow,
    staleTime: 60_000,
  });

  // Lazy backward history: when the user scrolls left past the loaded
  // range, PriceChart asks for the chunk ending just before `beforeIso`.
  // Clamped to the coverage's first_ts so the requests stop at 2015 (or
  // wherever this symbol's history begins) instead of probing forever.
  const loadMoreHistory = async (beforeIso: string): Promise<BarOut[]> => {
    if (!selected || !tfRow) return [];
    const before = new Date(beforeIso);
    const firstTs = tfRow.first_ts ? new Date(tfRow.first_ts + "Z") : null;
    if (firstTs && before <= firstTs) return [];
    const windowMs =
      INITIAL_BARS * tfMillis(timeframe) * sessionDensityFactor(selected.exchange);
    let start = new Date(before.getTime() - windowMs);
    if (firstTs && start < firstTs) start = firstTs;
    const r = await getBars({
      symbol: selected.symbol,
      exchange: selected.exchange,
      timeframe,
      instrument_type: selected.instrument_type,
      expiry: selected.expiry,
      strike: selected.strike,
      start: start.toISOString(),
      end: before.toISOString(), // /bars end is exclusive — no overlap
    });
    return r.bars;
  };

  // --- Auto gap-fill -------------------------------------------------------
  // When the store's data for the selected TF ends well before now, ask the
  // backend to backfill the missing window: selected TF first (tracked as the
  // "priority job" so we can refetch the chart when it lands), all other
  // stored TFs in the background. Fired at most once per symbol+TF per visit;
  // the server also dedupes (active job / job created in the last 10 min).
  const gapFillFiredRef = useRef<Set<string>>(new Set());
  const [gapJobId, setGapJobId] = useState<string | null>(null);

  const gapFill = useMutation({
    mutationFn: createGapFill,
    onSuccess: (res) => {
      if (res.priority_job_id) setGapJobId(res.priority_job_id);
    },
  });

  const gapMs = useMemo(() => {
    if (!tfRow?.last_ts) return 0;
    return Date.now() - new Date(tfRow.last_ts + "Z").getTime();
  }, [tfRow?.last_ts]);

  useEffect(() => {
    if (!selected || !tfRow) return;
    // Live-tail already covers the last ~3 bars; only backfill real holes.
    if (gapMs <= 3 * tfMillis(timeframe)) return;
    const key = `${selected.id}:${timeframe}`;
    if (gapFillFiredRef.current.has(key)) return;
    gapFillFiredRef.current.add(key);
    gapFill.mutate({
      symbol: selected.symbol,
      exchange: selected.exchange,
      instrument_type: selected.instrument_type,
      expiry: selected.expiry,
      strike: selected.strike,
      timeframe,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, timeframe, gapMs, tfRow]);

  // Poll the priority job; when it succeeds, refetch the chart window +
  // coverage so the filled bars render.
  const gapJob = useQuery({
    queryKey: ["gap-fill-job", gapJobId],
    queryFn: () => getBackfill(gapJobId!),
    enabled: !!gapJobId,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "PENDING" || s === "RUNNING" ? 2_000 : false;
    },
  });
  const gapStatus = gapJobId ? gapJob.data?.status : undefined;
  useEffect(() => {
    if (gapStatus === "SUCCESS") {
      qc.invalidateQueries({ queryKey: ["chart-bars", selected?.id, timeframe] });
      // ["data-coverage"] until 2026-08-11 — a query this page no longer uses,
      // so newly-filled bars didn't show up until the cache TTL lapsed.
      qc.invalidateQueries({ queryKey: ["instrument-coverage", selected?.id] });
      qc.invalidateQueries({ queryKey: ["instrument-search"] });
      // Downloading a NEW instrument is no longer this path's job — that goes
      // through `backfillInstrument`, which switches the chart immediately
      // rather than waiting for a job that gap-fill never created.
      setGapJobId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gapStatus]);

  // --- Live tail polling ---------------------------------------------------
  const liveQuery = useQuery({
    queryKey: ["chart-live-tail", selected?.id, timeframe],
    queryFn: () =>
      getLiveTail({
        symbol: selected!.symbol,
        exchange: selected!.exchange,
        timeframe,
        instrument_type: selected!.instrument_type,
        expiry: selected!.expiry,
        strike: selected!.strike,
      }),
    enabled: live && !!selected && !!barsQuery.data,
    refetchInterval: LIVE_POLL_MS,
    refetchIntervalInBackground: false,
  });

  // Live bars handed to PriceChart. Guard against stale responses from a
  // previous symbol/timeframe by keying on the query's own dataUpdatedAt.
  const liveBars: BarOut[] = useMemo(
    () => (live ? (liveQuery.data?.bars ?? []) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [liveQuery.data, liveQuery.dataUpdatedAt, live],
  );

  // --- Indicators ----------------------------------------------------------
  const [indicators, setIndicators] = useState<IndicatorInstance[]>([]);
  const [editingIndicator, setEditingIndicator] =
    useState<IndicatorInstance | null>(null);

  // --- Strategy overlay ----------------------------------------------------
  // Pick a strategy from the catalog and plot ITS indicators (the
  // `default_indicators` it declares, with $param placeholders resolved
  // against its default params) on the live chart. Purely visual — no
  // signals, no orders. Switching strategies swaps only the overlay's own
  // indicators; manually-added ones are untouched.
  const [overlayKey, setOverlayKey] = useState<string>("");
  const overlayIdsRef = useRef<Set<string>>(new Set());
  const strategies = useQuery({
    queryKey: ["strategies"],
    queryFn: listStrategies,
    staleTime: 300_000,
  });
  const overlayDetail = useQuery({
    queryKey: ["strategy", overlayKey],
    queryFn: () => getStrategy(overlayKey),
    enabled: !!overlayKey,
    staleTime: 300_000,
  });
  useEffect(() => {
    const prev = overlayIdsRef.current;
    const specs = overlayKey ? (overlayDetail.data?.default_indicators ?? null) : [];
    if (specs === null) return; // detail still loading — keep current overlay
    const fresh: IndicatorInstance[] = [];
    for (const spec of specs) {
      const inst = buildInstanceFromSpec(
        {
          type: spec.type,
          params: spec.params,
          pane: spec.pane,
          color: spec.color ?? undefined,
          label: spec.label ?? undefined,
        },
        overlayDetail.data?.default_params ?? {},
      );
      if (inst) fresh.push(inst);
    }
    overlayIdsRef.current = new Set(fresh.map((i) => i.id));
    setIndicators((cur) => [...cur.filter((i) => !prev.has(i.id)), ...fresh]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayKey, overlayDetail.data]);

  const overlayHasNoIndicators =
    !!overlayKey &&
    overlayDetail.data !== undefined &&
    overlayDetail.data.default_indicators.length === 0 &&
    !isGoldenFamily(overlayKey);

  // Golden Number overlay: the golden step-line isn't a normal indicator —
  // it's a per-session level (first-candle volume-profile POC) drawn via
  // PriceChart's dedicated goldenByDay channel. A generic /bars/golden-numbers
  // endpoint computes it for any instrument, so the overlay works without a
  // deployment.
  const goldenQuery = useQuery({
    queryKey: ["chart-golden", selected?.id, timeframe],
    queryFn: async () => {
      const params = new URLSearchParams({
        symbol: selected!.symbol,
        exchange: selected!.exchange,
        instrument_type: selected!.instrument_type,
        timeframe,
        start: new Date(Date.now() - 30 * 24 * 3600_000).toISOString(),
        end: new Date(Date.now() + 24 * 3600_000).toISOString(),
      });
      if (selected!.expiry) params.set("expiry", selected!.expiry);
      if (selected!.strike) params.set("strike", selected!.strike);
      const { apiFetch } = await import("../lib/api");
      return apiFetch<{ day: string; value: string }[]>(
        `/bars/golden-numbers?${params.toString()}`,
      );
    },
    enabled: isGoldenFamily(overlayKey) && !!selected,
    staleTime: 300_000,
  });
  const goldenByDay = useMemo<Record<string, number> | undefined>(() => {
    if (!isGoldenFamily(overlayKey)) return undefined;
    const map: Record<string, number> = {};
    for (const g of goldenQuery.data ?? []) {
      const v = Number(g.value);
      if (Number.isFinite(v)) map[g.day] = v;
    }
    return Object.keys(map).length ? map : undefined;
  }, [overlayKey, goldenQuery.data]);

  // --- Fullscreen ----------------------------------------------------------
  const fullscreenRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    function handler() {
      setIsFullscreen(document.fullscreenElement === fullscreenRef.current);
    }
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // --- Render --------------------------------------------------------------
  if (resolved.isLoading || fallback.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-96" />
        <Skeleton className="h-[480px]" />
      </div>
    );
  }

  if (!selected && !resolved.isLoading && !fallback.isLoading) {
    return (
      <EmptyState
        icon={<CandlestickChart size={32} />}
        title="No data in the store yet"
        message="Run a backfill on the Data page first — every backfilled symbol becomes chartable here."
      />
    );
  }

  // "checking" matters: this used to render a green pulsing "Live" whenever
  // liveQuery.data was undefined — i.e. it claimed live while it had no idea.
  // A stale chart labelled Live is worse than one labelled unknown.
  const liveState: "on" | "off" | "unavailable" | "checking" = !live
    ? "off"
    : !liveQuery.data
      ? "checking"
      : liveQuery.data.live
        ? "on"
        : "unavailable";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <SymbolSearch
          value={selected}
          onChange={selectInstrument}
          onRequestDownload={downloadInstrument}
        />
        <div className="inline-flex rounded border border-border overflow-hidden">
          {availableTfs.map((tf) => (
            <button
              key={tf}
              onClick={() => selectTimeframe(tf)}
              className={`px-3 py-1.5 text-xs font-mono transition-colors ${
                tf === timeframe
                  ? "bg-brand-600 text-white"
                  : "bg-surface text-fg-subtle hover:bg-surface-2"
              }`}
            >
              {tf}
            </button>
          ))}
        </div>
        {selected && missingTfs.length > 0 && (
          <Button
            variant="secondary"
            onClick={getMissingTimeframes}
            disabled={bootstrap.isPending || bootstrapJobIds.length > 0}
            title={`Not downloaded yet: ${missingTfs.join(", ")}`}
            iconLeft={<Download size={14} />}
          >
            Get all timeframes
          </Button>
        )}
        <LiveToggle
          state={liveState}
          detail={liveQuery.data?.detail ?? null}
          onToggle={() => setLive((v) => !v)}
        />
        {liveState === "unavailable" && liveQuery.data?.detail && (
          <LiveProblem detail={liveQuery.data.detail} />
        )}
        <div
          className="flex items-center gap-1.5"
          title="Plot a strategy's indicators on this chart (visual only — no signals or orders)"
        >
          <span className="text-xs text-muted uppercase tracking-wide">
            Strategy:
          </span>
          <select
            value={overlayKey}
            onChange={(e) => setOverlayKey(e.target.value)}
            className="bg-surface border border-border rounded px-2 py-1.5 text-xs"
          >
            <option value="">none</option>
            {(strategies.data ?? []).map((s) => (
              <option key={s.key} value={s.key}>
                {s.display_name}
              </option>
            ))}
          </select>
          {overlayHasNoIndicators && (
            <span className="text-[11px] text-warn-600 dark:text-warn-400">
              declares no chart indicators
            </span>
          )}
        </div>
        <GapFillChip
          pending={gapFill.isPending}
          jobStatus={gapJobId ? gapJob.data?.status : undefined}
          error={gapJob.data?.error_message ?? gapFill.data?.detail ?? null}
          onRetry={() => {
            if (!selected) return;
            // Clear the once-per-visit latch and re-fire. The backend no
            // longer suppresses retries after a FAILED job and will extend
            // the window back over the hole the failed attempt left.
            gapFillFiredRef.current.delete(`${selected.id}:${timeframe}`);
            setGapJobId(null);
            gapFill.mutate({
              symbol: selected.symbol,
              exchange: selected.exchange,
              instrument_type: selected.instrument_type,
              expiry: selected.expiry,
              strike: selected.strike,
              timeframe,
            });
          }}
        />
        <BootstrapPanel
          jobs={bootstrapJobs.data ?? []}
          note={bootstrapNote}
          onDismiss={() => {
            setBootstrapJobIds([]);
            setBootstrapNote(null);
          }}
        />
        <div className="ml-auto">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              const el = fullscreenRef.current;
              if (!el) return;
              if (!document.fullscreenElement) el.requestFullscreen?.();
              else document.exitFullscreen?.();
            }}
            title="Press Esc to exit fullscreen"
          >
            {isFullscreen ? "× Exit fullscreen" : "⛶ Fullscreen"}
          </Button>
        </div>
      </div>

      <IndicatorsPanel
        indicators={indicators}
        onAdd={(inst) => setIndicators((cur) => [...cur, inst])}
        onRemove={(id) => setIndicators((cur) => cur.filter((i) => i.id !== id))}
        onEdit={(inst) => setEditingIndicator(inst)}
      />

      <div
        ref={fullscreenRef}
        className={isFullscreen ? "bg-surface p-4 h-screen w-screen flex flex-col" : ""}
      >
        {barsQuery.isLoading ? (
          <Skeleton className="h-[560px]" />
        ) : (
          <PriceChart
            bars={barsQuery.data?.bars ?? EMPTY}
            liveBars={liveBars}
            onLoadMoreHistory={loadMoreHistory}
            trades={EMPTY}
            indicators={indicators}
            onIndicatorChange={(id, patch) =>
              setIndicators((cur) =>
                cur.map((i) => (i.id === id ? { ...i, ...patch } : i)),
              )
            }
            editingIndicator={editingIndicator}
            onCloseIndicatorEdit={() => setEditingIndicator(null)}
            watermark={selected?.display}
            timeframe={timeframe}
            goldenByDay={goldenByDay}
            instrument={
              selected
                ? {
                    symbol: selected.symbol,
                    exchange: selected.exchange,
                    instrument_type: selected.instrument_type,
                    expiry: selected.expiry,
                    strike: selected.strike,
                  }
                : undefined
            }
            heightClass={isFullscreen ? "h-full" : "h-[560px]"}
          />
        )}
      </div>

      {downloadTarget && (
        <DownloadDialog
          target={downloadTarget}
          pending={bootstrap.isPending}
          onCancel={() => setDownloadTarget(null)}
          onConfirm={(from, to) =>
            bootstrap.mutate({
              symbol: downloadTarget.symbol,
              exchange: downloadTarget.exchange,
              instrument_type: downloadTarget.instrument_type,
              expiry: downloadTarget.expiry,
              strike: downloadTarget.strike,
              lot_size: downloadTarget.lot_size,
              tick_size: downloadTarget.tick_size,
              timeframes: downloadTarget.timeframes,
              start: `${from}T00:00:00Z`,
              end: `${to}T23:59:59Z`,
            })
          }
        />
      )}
    </div>
  );
}

/** Date-range prompt for a download. There is no Modal component in this
 *  codebase — every confirmation is `window.confirm`, which cannot host inputs
 *  — so this is an inline popover in the same style as the page's other
 *  explain-and-act blocks. */
function DownloadDialog({
  target,
  pending,
  onCancel,
  onConfirm,
}: {
  target: DownloadTarget;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (start: string, end: string) => void;
}) {
  const [from, setFrom] = useState(isoDaysAgo(365));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const invalid = !from || !to || from >= to;
  const what =
    target.timeframes === null
      ? "all 8 timeframes"
      : `${target.timeframes.length} missing timeframe${
          target.timeframes.length === 1 ? "" : "s"
        } (${target.timeframes.join(", ")})`;

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center pt-32 bg-black/30">
      <div className="bg-surface border border-border rounded-lg shadow-card w-full max-w-[440px] p-4 space-y-3">
        <div className="text-sm font-semibold text-fg">Download {target.label}</div>
        <div className="text-xs text-muted">
          Fetching {what}. Coarser timeframes are fetched first, so the daily
          chart appears within seconds while the finer ones continue behind it.
          A longer range means more broker requests — 1-minute data is by far
          the slowest.
        </div>
        <div className="flex items-end gap-2">
          <label className="text-xs text-muted">
            From
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              className="block bg-bg border border-border rounded px-2 py-1 text-sm text-fg"
            />
          </label>
          <label className="text-xs text-muted">
            To
            <input
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
              className="block bg-bg border border-border rounded px-2 py-1 text-sm text-fg"
            />
          </label>
          <div className="flex gap-1 pb-0.5">
            {[
              ["1y", 365],
              ["3y", 365 * 3],
              ["5y", 365 * 5],
            ].map(([label, days]) => (
              <button
                key={label as string}
                type="button"
                onClick={() => setFrom(isoDaysAgo(days as number))}
                className="text-[11px] px-1.5 py-1 rounded border border-border text-muted hover:text-fg"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(from, to)}
            disabled={invalid || pending}
            iconLeft={<Download size={14} />}
          >
            {pending ? "Starting…" : "Download"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Multi-job progress for a bootstrap, plus anything the backend refused.
 *  The old chip modelled a single job and rendered `null` for every state that
 *  wasn't active or failed — which is how a refusal became invisible. */
function BootstrapPanel({
  jobs,
  note,
  onDismiss,
}: {
  jobs: BackfillJob[];
  note: BootstrapNote | null;
  onDismiss: () => void;
}) {
  const done = jobs.filter((j) => j.status === "SUCCESS").length;
  const failed = jobs.filter((j) => j.status === "FAILED").length;
  const running = jobs.find((j) => j.status === "RUNNING");
  const active = jobs.some((j) => j.status === "PENDING" || j.status === "RUNNING");
  if (jobs.length === 0 && !note?.detail && !note?.skipped.length) return null;

  return (
    <div className="text-xs bg-surface border border-border rounded px-3 py-2 space-y-1 max-w-[420px]">
      {jobs.length > 0 && (
        <div className="text-fg">
          {active ? (
            <span className="animate-pulse">
              Downloading {done} of {jobs.length}
              {running ? ` · ${running.timeframe} now` : ""}
            </span>
          ) : (
            <span>
              Downloaded {done} of {jobs.length} timeframes
              {failed > 0 ? ` · ${failed} failed` : ""}
            </span>
          )}
        </div>
      )}
      {jobs
        .filter((j) => j.status === "FAILED")
        .map((j) => (
          <div key={j.id} className="text-down-600 dark:text-down-400">
            {j.timeframe}: {j.error_message ?? "failed"}
          </div>
        ))}
      {note?.detail && (
        <div className="text-down-600 dark:text-down-400">{note.detail}</div>
      )}
      {note?.skipped.map((s) => (
        <div key={s.timeframe} className="text-warn-700 dark:text-warn-400">
          {s.timeframe} skipped — {s.reason}
        </div>
      ))}
      {!active && (
        <button
          type="button"
          onClick={onDismiss}
          className="text-muted hover:text-fg underline"
        >
          Dismiss
        </button>
      )}
    </div>
  );
}

function GapFillChip({
  pending,
  jobStatus,
  error,
  onRetry,
}: {
  pending: boolean;
  jobStatus: string | undefined;
  error: string | null;
  onRetry: () => void;
}) {
  const active = pending || jobStatus === "PENDING" || jobStatus === "RUNNING";
  const failed = jobStatus === "FAILED";
  if (!active && !failed) return null;
  if (failed) {
    return (
      <button
        type="button"
        onClick={onRetry}
        title={`${error ?? "Gap fill failed"} — click to retry (e.g. after refreshing your broker token)`}
        className="inline-flex items-center gap-2 border border-down-200 dark:border-down-400/30 bg-down-50 dark:bg-down-500/10 text-down-700 dark:text-down-400 hover:bg-down-100 dark:hover:bg-down-500/20 rounded px-3 py-1.5 text-xs"
      >
        Gap fill failed — retry
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 border border-warn-200 dark:border-warn-400/30 bg-warn-50 dark:bg-warn-500/10 text-warn-700 dark:text-warn-400 rounded px-3 py-1.5 text-xs">
      <span className="inline-block h-2 w-2 rounded-full bg-warn-500 animate-pulse" />
      Filling gap…
    </span>
  );
}

function LiveToggle({
  state,
  detail,
  onToggle,
}: {
  state: "on" | "off" | "unavailable" | "checking";
  detail: string | null;
  onToggle: () => void;
}) {
  const dot =
    state === "on"
      ? "bg-up-500 animate-pulse"
      : state === "unavailable"
        ? "bg-warn-500"
        : state === "checking"
          ? "bg-border animate-pulse"
          : "bg-border";
  const label =
    state === "on"
      ? "Live"
      : state === "unavailable"
        ? "Live unavailable"
        : state === "checking"
          ? "Checking…"
          : "Live off";
  return (
    <button
      type="button"
      onClick={onToggle}
      title={detail ?? (state === "on" ? "Polling the source every 5s" : "Click to toggle live updates")}
      className="inline-flex items-center gap-2 border border-border rounded px-3 py-1.5 text-xs text-fg-subtle hover:bg-surface-2"
    >
      <span className={`inline-block h-2 w-2 rounded-full ${dot}`} />
      <Radio size={12} className="text-muted" />
      {label}
    </button>
  );
}


/** Surfaces WHY live data stopped, inline rather than in a tooltip.
 *
 *  The overwhelmingly common cause is an expired broker token — Kite's die at
 *  ~06:00 IST daily — and until now that produced a chart quietly stuck on
 *  yesterday's candles with the reason hidden behind a hover. */
function LiveProblem({ detail }: { detail: string }) {
  const isToken = /token|api_key|access_token|unauthor|forbidden/i.test(detail);
  return (
    <span
      title={detail}
      className="inline-flex items-center gap-2 border border-warn-200 dark:border-warn-400/30 bg-warn-50 dark:bg-warn-500/10 text-warn-700 dark:text-warn-400 rounded px-3 py-1.5 text-xs max-w-[420px]"
    >
      <span className="truncate">
        {isToken
          ? "Your broker token has expired — today's candles can't load."
          : detail}
      </span>
      {isToken && (
        <Link to="/brokers" className="underline shrink-0 whitespace-nowrap">
          Refresh
        </Link>
      )}
    </span>
  );
}
